import {inject, observer} from 'mobx-react';
import * as React from 'react';
import {IInjectedProps} from '../../interface/component/injected-props.interface';
import {addOpacity} from '../../util/style/add-opacity.util';
import styled, {keyframes} from 'styled-components';
import {ColorEnum} from '../../enum/color.enum';
import {CrossIcon, ErrorIcon} from '../icons';
import * as ReactDOM from 'react-dom';
import {action, observable} from 'mobx';
import {DesignTypeEnum} from '../../enum/design-type.enum';
import {BlockTypeEnum} from '../../enum/block/block-type.enum';

const ANIMATION_DURATION_MS = 250;
const closeMessageAnimation = (height) => keyframes`
  0% {
    max-height: ${height}px;
  }
  100% {
    max-height: 0px;
  }
`;
const openMessageAnimation = (height) => keyframes`
  0% {
    max-height: 0px;
  }
  100% {
    max-height: ${height}px;
  }
`;

interface IAnimationWrapperProps {
  closing: boolean;
  contentHeight: number;
}
const AnimationWrapper = styled.div`
  overflow: hidden;
  opacity: ${(props: IAnimationWrapperProps) => !Boolean(props.contentHeight) ? 0 : 1};
  max-height: ${(props: IAnimationWrapperProps) => props.closing ? '0px' : 'auto'};
  animation: ${(props: IAnimationWrapperProps) => props.closing ? closeMessageAnimation(props.contentHeight) : (Boolean(props.contentHeight) ? openMessageAnimation(props.contentHeight) : 'none')} ${ANIMATION_DURATION_MS}ms ease-in;
`;
AnimationWrapper.displayName = 'AnimationWrapperIeWarning';

const ErrorMessageContainer = styled.div`
  display:flex;
  justify-content: center;
  align-content: center;
  text-align: center;
  border-radius: 3px;
  margin-bottom: 14px;
  width: 100%;
  padding: 13px;
  flex-direction: row;
  color: ${ColorEnum.DarkBlack};
  pointer-events: all;
  background: ${(props) => addOpacity(ColorEnum.White(props), 0.85)};
`;

const ErrorIconContainer = styled.div`
  margin-right: 18px;
`;

const TitleAndMessageContainer = styled.div`
  display:flex;
  flex-direction: column;
  flex: 1;
  text-align: left;
`;
const CloseIconContainer = styled.div`
  margin-left: 18px;
  cursor: pointer;
`;
const TitleContainer = styled.span`
  font-size: 18px;
  font-weight: bold;
`;
const InfoMessageContainer = styled.div`
  font-size: 16px;
  font-weight: normal;
`;

const HORIZONTAL_MARGIN = 12;
const VERTICAL_MARGIN = 14;
const MessagesContainer = styled.div`
  position: fixed;
  width: calc(100% - ${HORIZONTAL_MARGIN * 2}px);
  top: 0;
  margin: ${VERTICAL_MARGIN}px ${HORIZONTAL_MARGIN}px;
  z-index: 999999;
  overflow: auto;
  pointer-events: none;
`;
MessagesContainer.displayName = 'MessagesContainerIeWarning';

@inject('state', 'dispatch')
@observer
export class IeWarning extends React.Component<IInjectedProps> {
  messageBox;
  @observable closing = false;
  @observable contentHeight = 0;

  @action close = () => {
    if (this.messageBox) {
      this.contentHeight = this.messageBox.getBoundingClientRect().height;
      this.closing = true;
    }
  }

  @action componentDidMount() {
    if (this.messageBox) {
      this.contentHeight = this.messageBox.getBoundingClientRect().height;
    }
  }

  render() {
    if (this.props.state.configuration.inAdmin) {
      return null;
    }

    if (!this.props.state.configuration.isExplorer) {
      return null;
    }

    // Don't display IE warning in JobAd pages containing Form
    if (this.props.state.activeDesign.data.type === DesignTypeEnum.JobAdPage) {
      const foundForm = this.props.state.activeDesign.data.blocks.find(x => x.blockType === BlockTypeEnum.Form);
      if (foundForm) {
        return null;
      }
    }

    const lines = this.props.state.adminSettings.errorMessageIeWarning.split(/\r?\n/);
    const line1 = lines.length > 0 ? lines[0] : '';
    const line2 = lines.length > 1 ? lines[1] : '';
    const restLines = lines.length > 2 ? lines.slice(2, lines.length) : [];

    return (
        <MessagesContainer>
          <AnimationWrapper
            ref={(ref) => this.messageBox = ReactDOM.findDOMNode(ref)}
            contentHeight={this.contentHeight}
            closing={this.closing}
          >
            <ErrorMessageContainer>
              <ErrorIconContainer>
                <ErrorIcon
                  color={this.props.state.colors.DarkBlack}
                  style={{ opacity: 0.7 }}
                />
              </ErrorIconContainer>
              <TitleAndMessageContainer>
                <TitleContainer>{line1}</TitleContainer>
                <p />
                <InfoMessageContainer>{line2}</InfoMessageContainer>
                <p />
                {restLines.map(x => <InfoMessageContainer key={x}>{x}</InfoMessageContainer>)}
              </TitleAndMessageContainer>
              <CloseIconContainer onClick={this.close}>
                <CrossIcon color={this.props.state.colors.DarkBlack} />
              </CloseIconContainer>
            </ErrorMessageContainer>
          </AnimationWrapper>
        </MessagesContainer>
    );
  }
}